import { useState, useEffect, createContext, useContext } from 'react';
import { Globe, ChevronDown, Check } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

// Create translation context
const TranslationContext = createContext();

export const useTranslation = () => {
  const context = useContext(TranslationContext);
  if (!context) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
};

// Translation Provider
export const TranslationProvider = ({ children }) => {
  const [currentLanguage, setCurrentLanguage] = useState('en');
  const [currentDialect, setCurrentDialect] = useState('es-MX');
  const [isTranslating, setIsTranslating] = useState(false);

  // Load saved preferences
  useEffect(() => {
    const savedLang = localStorage.getItem('jax_language') || 'en';
    const savedDialect = localStorage.getItem('jax_dialect') || 'es-MX';
    setCurrentLanguage(savedLang);
    setCurrentDialect(savedDialect);
  }, []);

  const changeLanguage = (lang) => {
    setCurrentLanguage(lang);
    localStorage.setItem('jax_language', lang);
  };

  const changeDialect = (dialect) => {
    setCurrentDialect(dialect);
    localStorage.setItem('jax_dialect', dialect);
  };

  const translateText = async (text, targetLang = currentLanguage) => {
    if (targetLang === 'en') return text;
    
    setIsTranslating(true);
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/translate/`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          text,
          source_language: 'en',
          target_language: targetLang === 'es' ? 'es' : targetLang.split('-')[0],
          dialect: targetLang.includes('-') ? targetLang : currentDialect
        })
      });
      const data = await response.json();
      return data.translated_text;
    } catch (error) {
      console.error('Translation error:', error);
      return text;
    } finally {
      setIsTranslating(false);
    }
  };

  const speakText = (text, lang = currentLanguage) => {
    if ('speechSynthesis' in window) {
      // Cancel any ongoing speech
      speechSynthesis.cancel();
      
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = lang === 'es' ? currentDialect : lang;
      
      // Try to find a voice for the selected dialect
      const voices = speechSynthesis.getVoices();
      const dialectVoice = voices.find(v => v.lang.startsWith(currentDialect.split('-')[0]));
      if (dialectVoice) {
        utterance.voice = dialectVoice;
      }
      
      speechSynthesis.speak(utterance);
    }
  };

  return (
    <TranslationContext.Provider value={{
      currentLanguage,
      currentDialect,
      changeLanguage,
      changeDialect,
      translateText,
      speakText,
      isTranslating,
      isSpanish: currentLanguage === 'es'
    }}>
      {children}
    </TranslationContext.Provider>
  );
};

// Common translations for UI elements
const UI_TRANSLATIONS = {
  en: {
    dashboard: 'Dashboard',
    jobs: 'Jobs Pipeline',
    team: 'Team Builder',
    inventory: 'Inventory',
    vehicles: 'Vehicle Fleet',
    documents: 'Documents',
    accounting: 'Accounting',
    liabilities: 'Liabilities',
    messages: 'Messages',
    settings: 'Settings',
    logout: 'Logout',
    search: 'Search',
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    edit: 'Edit',
    add: 'Add',
    loading: 'Loading...',
    noResults: 'No results found'
  },
  es: {
    dashboard: 'Panel',
    jobs: 'Proyectos',
    team: 'Equipo',
    inventory: 'Inventario',
    vehicles: 'Vehículos',
    documents: 'Documentos',
    accounting: 'Contabilidad',
    liabilities: 'Pasivos',
    messages: 'Mensajes',
    settings: 'Configuración',
    logout: 'Cerrar Sesión',
    search: 'Buscar',
    save: 'Guardar',
    cancel: 'Cancelar',
    delete: 'Eliminar',
    edit: 'Editar',
    add: 'Agregar',
    loading: 'Cargando...',
    noResults: 'Sin resultados'
  }
};

// Hook to get translated UI text
export const useUIText = (key) => {
  const { currentLanguage } = useTranslation();
  const lang = currentLanguage === 'es' ? 'es' : 'en';
  return UI_TRANSLATIONS[lang][key] || UI_TRANSLATIONS['en'][key] || key;
};

// Spanish Dialects
const SPANISH_DIALECTS = [
  { code: 'es-MX', name: 'Mexican Spanish', country: 'Mexico', flag: '🇲🇽' },
  { code: 'es-ES', name: 'Castilian Spanish', country: 'Spain', flag: '🇪🇸' },
  { code: 'es-GT', name: 'Guatemalan Spanish', country: 'Guatemala', flag: '🇬🇹' },
  { code: 'es-SV', name: 'Salvadoran Spanish', country: 'El Salvador', flag: '🇸🇻' },
  { code: 'es-HN', name: 'Honduran Spanish', country: 'Honduras', flag: '🇭🇳' },
  { code: 'es-CO', name: 'Colombian Spanish', country: 'Colombia', flag: '🇨🇴' },
  { code: 'es-VE', name: 'Venezuelan Spanish', country: 'Venezuela', flag: '🇻🇪' }
];

// Language Selector Component
export const LanguageSelector = () => {
  const { currentLanguage, currentDialect, changeLanguage, changeDialect } = useTranslation();
  const [showDropdown, setShowDropdown] = useState(false);

  const selectedDialect = SPANISH_DIALECTS.find(d => d.code === currentDialect) || SPANISH_DIALECTS[0];

  return (
    <div className="relative">
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors"
        data-testid="language-selector"
      >
        <Globe className="w-4 h-4 text-gray-600" />
        <span className="text-sm font-medium">
          {currentLanguage === 'en' ? 'EN' : selectedDialect.flag}
        </span>
        <ChevronDown className="w-4 h-4 text-gray-400" />
      </button>

      <AnimatePresence>
        {showDropdown && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            className="absolute right-0 top-full mt-2 w-64 bg-white border border-gray-200 rounded-xl shadow-xl z-50 overflow-hidden"
          >
            <div className="p-2">
              {/* English Option */}
              <button
                onClick={() => {
                  changeLanguage('en');
                  setShowDropdown(false);
                }}
                className={`w-full flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 ${
                  currentLanguage === 'en' ? 'bg-[#C6A87C]/10' : ''
                }`}
              >
                <div className="flex items-center gap-3">
                  <span className="text-xl">🇺🇸</span>
                  <div className="text-left">
                    <p className="font-medium text-sm">English</p>
                    <p className="text-xs text-gray-500">United States</p>
                  </div>
                </div>
                {currentLanguage === 'en' && <Check className="w-4 h-4 text-[#C6A87C]" />}
              </button>

              <div className="my-2 border-t border-gray-100" />
              
              <p className="px-3 py-1 text-xs font-semibold text-gray-500 uppercase">Spanish Dialects</p>

              {/* Spanish Dialect Options */}
              {SPANISH_DIALECTS.map((dialect) => (
                <button
                  key={dialect.code}
                  onClick={() => {
                    changeLanguage('es');
                    changeDialect(dialect.code);
                    setShowDropdown(false);
                  }}
                  className={`w-full flex items-center justify-between p-3 rounded-lg hover:bg-gray-50 ${
                    currentLanguage === 'es' && currentDialect === dialect.code ? 'bg-[#C6A87C]/10' : ''
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xl">{dialect.flag}</span>
                    <div className="text-left">
                      <p className="font-medium text-sm">{dialect.country}</p>
                      <p className="text-xs text-gray-500">{dialect.name}</p>
                    </div>
                  </div>
                  {currentLanguage === 'es' && currentDialect === dialect.code && (
                    <Check className="w-4 h-4 text-[#C6A87C]" />
                  )}
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default LanguageSelector;
